import {
  getRefundAccountWithSettings,
  deleteRefundAccount,
  saveRefundAccount,
} from '../../api/api'
import { call, put, takeLatest, takeEvery } from 'redux-saga/effects'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import { addError } from 'portal-modules/common/redux/modules/errors'
import { addSettings } from 'portal-modules/common/redux/modules/settings'

// action types
const REQUEST_REFUND_METHOD = 'rp/refundMethod/REQUEST_REFUND_METHOD'
const RECEIVE_REFUND_METHOD = 'rp/refundMethod/RECEIVE_REFUND_METHOD'
const DELETE_REFUND_METHOD = 'rp/refundMethod/DELETE_REFUND_METHOD'
const REFUND_METHOD_DELETED = 'rp/refundMethod/REFUND_METHOD_DELETED'
const ADD_REFUND_METHOD = 'rp/refundMethod/ADD_REFUND_METHOD'
const REFUND_METHOD_ADDED = 'rp/refundMethod/REFUND_METHOD_ADDED'
const UPDATE_REFUND_METHOD = 'rp/refundMethod/UPDATE_REFUND_METHOD'
const REFUND_METHOD_UPDATED = 'rp/refundMethod/REFUND_METHOD_UPDATED'

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_REFUND_METHOD:
      return { ...state, ...payload }
    case REFUND_METHOD_DELETED:
      return Object.values(state).reduce((obj, method) => {
        if (method.id === payload) return obj
        return { ...obj, [method.id]: method }
      }, {})
    case REFUND_METHOD_ADDED:
      return { ...state, [payload.id]: { ...payload } }
    case REFUND_METHOD_UPDATED:
      return {
        ...state,
        [payload.id]: { ...(state[payload.id] || {}), ...payload },
      }
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators
export const requestRefundMethod = () => ({
  type: REQUEST_REFUND_METHOD,
})

export const receiveRefundMethod = payload => ({
  type: RECEIVE_REFUND_METHOD,
  payload,
})

export const deleteRefundMethod = (payload, callback) => ({
  type: DELETE_REFUND_METHOD,
  callback,
  payload,
})

export const addRefundMethod = createFormAction(ADD_REFUND_METHOD)

export const updateRefundMethod = createFormAction(UPDATE_REFUND_METHOD)

export function* handleRefundMethodError(
  { message, fields = [] },
  { key, action: { onError } = {} },
) {
  // Field-level validation errors (1002) carry a `field_name`. Forward them to
  // the Form's submit-error setter (passed through `action.onError`) so they
  // render inline on the matching field. Field names are the raw backend keys
  // (snake_case); the form maps them to its own field names.
  const keyedFields = fields.filter(field => field.field_name)
  if (typeof onError === 'function' && keyedFields.length) {
    yield call(
      onError,
      keyedFields.reduce(
        (obj, { field_name, message: fieldMessage }) => ({
          ...obj,
          [field_name]: fieldMessage,
        }),
        {},
      ),
    )
  }

  // Operation-level errors (duplicate account, not eligible, ACH not allowed,
  // etc.) have no `field_name`. Surface them as a banner via the error store.
  const unkeyedFields = fields.filter(field => !field.field_name)
  if (keyedFields.length === 0 || unkeyedFields.length) {
    const generalMessage = unkeyedFields.length
      ? unkeyedFields[0].message
      : message
    yield put(addError({ key, message: generalMessage }))
  }
}

// sagas
function* fetchRefundMethod() {
  const refundMethods = yield call(getRefundAccountWithSettings)

  const { settings, refund_accounts } = refundMethods

  if (Array.isArray(refund_accounts)) {
    yield put(
      receiveRefundMethod(
        refund_accounts.reduce(
          (obj, method) => ({
            ...obj,
            [method.id]: method,
          }),
          {},
        ),
      ),
    )
  }

  yield put(addSettings({ refundMethod: settings }))
}

function* postDeleteRefundMethod({ payload, callback }) {
  yield call(deleteRefundAccount, {
    id: payload,
  })

  yield put({ type: REFUND_METHOD_DELETED, payload })

  if (typeof callback === 'function') {
    yield call(callback)
  }
}

function* postAddRefundMethod({ payload = {}, callback }) {
  const addedMethod = yield call(saveRefundAccount, payload)

  yield put({
    type: REFUND_METHOD_ADDED,
    payload: addedMethod,
  })

  if (typeof callback === 'function') {
    yield call(callback, addedMethod)
  }
}

function* postUpdateRefundMethod({ payload = {}, callback }) {
  const updatedMethod = yield call(saveRefundAccount, payload)

  yield put({
    type: REFUND_METHOD_UPDATED,
    payload: updatedMethod,
  })

  if (typeof callback === 'function') {
    yield call(callback, updatedMethod)
  }
}

function* watchRequestRefundMethod() {
  yield takeLatest(
    REQUEST_REFUND_METHOD,
    createRequestSaga(fetchRefundMethod, {
      key: 'refundMethod',
      message: __('Finding refund method'),
    }),
  )
}

function* watchDeleteRefundMethod() {
  yield takeEvery(
    DELETE_REFUND_METHOD,
    createRequestSaga(postDeleteRefundMethod, {
      key: 'refundMethod',
      message: __('Deleting refund account'),
      successMessage: __('Refund account deleted'),
    }),
  )
}

function* watchAddRefundMethod() {
  yield takeEvery(
    ADD_REFUND_METHOD,
    createRequestSaga(postAddRefundMethod, {
      key: 'refundMethod',
      message: __('Adding refund account'),
      successMessage: __('Refund account added'),

      onError: handleRefundMethodError,
    }),
  )
}

function* watchUpdateRefundMethod() {
  yield takeEvery(
    UPDATE_REFUND_METHOD,
    createRequestSaga(postUpdateRefundMethod, {
      key: 'refundMethod',
      message: __('Updating refund account'),
      successMessage: __('Refund account updated'),

      onError: handleRefundMethodError,
    }),
  )
}

export const sagas = [
  watchRequestRefundMethod,
  watchDeleteRefundMethod,
  watchAddRefundMethod,
  watchUpdateRefundMethod,
]
